export const ENTITY_TYPES = {
  companies: { label: "Companies", icon: "ph-buildings", chip: "bg-blue-100 text-blue-700 border-blue-200" },
  employees: { label: "Employees", icon: "ph-users-three", chip: "bg-purple-100 text-purple-700 border-purple-200" },
  updates: { label: "Updates", icon: "ph-newspaper", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  funding: { label: "Funding", icon: "ph-currency-circle-dollar", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  specialties: { label: "Specialties", icon: "ph-tag-chevron", chip: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  addresses: { label: "Addresses", icon: "ph-map-pin", chip: "bg-rose-100 text-rose-700 border-rose-200" },
  work_experience: { label: "Work Experience", icon: "ph-briefcase", chip: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  education_history: { label: "Education History", icon: "ph-graduation-cap", chip: "bg-teal-100 text-teal-700 border-teal-200" },
  monitor: { label: "Monitor", icon: "ph-binoculars", chip: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

export const JOB_STATUS = Object.freeze({
  PENDING: "pending",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
  ABORTED: "aborted",
});

export const JOB_STATUS_CLEARS_LOADING = new Set([JOB_STATUS.COMPLETED, JOB_STATUS.ABORTED]);
export const JOB_STATUS_ACTIVE = new Set([JOB_STATUS.PENDING, JOB_STATUS.RUNNING]);

const PROSPECTOR_UI_SCHEMA = (typeof window !== "undefined" && window.PROSPECTOR_UI_SCHEMA) || {};

function sample(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

const DEMO_COLUMN_ACTIONS = {
  companies: [
    { key: "employee_count", label: "Get employee count", icon: "ph-users", credits: 2,
      action_kind: "enrich_row_employee_count",
      columns: ["employee_count"],
      mockValueFor: () => sample([12, 47, 132, 540, 1100, 2800, 6700, 14200]) },
    { key: "funding_total", label: "Get funding data", icon: "ph-currency-circle-dollar", credits: 8,
      action_kind: "enrich_row_funding",
      columns: ["total_funds_raised_usd", "last_round_type", "last_round_date",
                "last_round_amount_usd", "lead_investor", "investor_count", "num_rounds"],
      mockValueFor: () => sample(["$12M Seed", "$45M Series A", "$110M Series B", "$300M Series C", "$1.2B Series D", "$95M Series A"]) },
  ],
  employees: [
    { key: "work_email", label: "Get Work Email", icon: "ph-envelope-simple", credits: 2,
      action_kind: "enrich_row_work_email",
      columns: ["work_email"],
      mockValueFor: (row) => {
        const parts = String((row && row.name) || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
        const first = parts[0] || "contact";
        const last = parts.length > 1 ? parts[parts.length - 1] : "";
        const domain = (row && row.company_domain) || "example.com";
        return `${first}${last ? "." + last : ""}@${domain}`;
      } },
  ],
  updates: [],
};

const COLUMN_ACTION_UI = {
  enrich_row_employee_count: {
    icon: "ph-users",
    mockValueFor: () => sample([12, 47, 132, 540, 1100, 2800, 6700, 14200]),
  },
  enrich_row_funding: {
    icon: "ph-currency-circle-dollar",
    mockValueFor: () => sample(["$12M Seed", "$45M Series A", "$110M Series B", "$300M Series C", "$1.2B Series D", "$95M Series A"]),
  },
  enrich_row_work_email: {
    icon: "ph-envelope-simple",
    mockValueFor: (row) => {
      const parts = String((row && row.name) || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
      const first = parts[0] || "contact";
      const last = parts.length > 1 ? parts[parts.length - 1] : "";
      const domain = (row && row.company_domain) || "example.com";
      return `${first}${last ? "." + last : ""}@${domain}`;
    },
  },
};

function normalizeColumnActions(actionsByEntity) {
  return Object.fromEntries(
    Object.entries(actionsByEntity || {}).map(([entityType, actions]) => [
      entityType,
      (actions || []).map(action => ({
        ...action,
        ...(COLUMN_ACTION_UI[action.action_kind] || {}),
      })),
    ])
  );
}

export const COLUMN_ACTIONS = Object.keys(PROSPECTOR_UI_SCHEMA.columnActions || {}).length
  ? normalizeColumnActions(PROSPECTOR_UI_SCHEMA.columnActions)
  : DEMO_COLUMN_ACTIONS;

export const EMPLOYEE_COUNT_ACTION_KIND = "enrich_row_employee_count";
export const WORK_EMAIL_ACTION_KIND = "enrich_row_work_email";
export const EMPLOYEE_COUNT_COLUMN_KEYS = ["employee_count"];

export const EMPLOYEE_PROFILE_REFRESH_KEYS = new Set(
  PROSPECTOR_UI_SCHEMA.employeeProfileRefreshKeys || [
    "full_name",
    "id", "slug", "profile_pic_url", "bio", "country", "city", "state",
    "x_handle", "x_profile_url", "follower_count", "following_count",
    "personal_website",
  ]
);

export const PERSON_ADD_ROW_METHOD_COLUMNS = Object.freeze(
  PROSPECTOR_UI_SCHEMA.personAddRowMethodColumns || {
    work_email: ["work_email"],
    name_employer: ["first_name", "middle_name", "last_name", "company", "company_domain"],
    employer_role: ["role", "company", "company_domain"],
  }
);

const DEMO_TRANSFORMS = {
  companies: [
    { key: "competitors", actionKey: "get-competitors", label: "Get competitors", icon: "ph-flag", yields: "companies", credits: 5, pricingLabel: "5 credit minimum/source", supportsResultLimit: true },
    { key: "customers", actionKey: "get-customers", label: "Get customers", icon: "ph-handshake", yields: "companies", credits: 10, pricingLabel: "1 credit base + 2/customer", supportsResultLimit: true },
    { key: "funding", actionKey: "get-funding", label: "Get funding", icon: "ph-currency-circle-dollar", yields: "funding", credits: 2, pricingLabel: "2 credit base + 1/investor", supportsResultLimit: true },
    { key: "search_emp", actionKey: "search-employees", label: "Search employees", icon: "ph-magnifying-glass", yields: "employees", credits: 2, pricingLabel: "2 credit base + 1/person",
      supportsResultLimit: true,
      inputFields: [
        { key: "role", label: "Role", required: true, placeholder: "CEO, VP Sales, Recruiter" },
        { key: "country", label: "Country", placeholder: "US", maxLength: 2 },
        { key: "state", label: "State", placeholder: "California" },
        { key: "city", label: "City", placeholder: "San Francisco" },
      ] },
    { key: "get_updates", actionKey: "get-updates", label: "Get updates", icon: "ph-rss", yields: "updates", credits: 2, pricingLabel: "2 credits/source", supportsResultLimit: true },
    { key: "monitor", actionKey: "monitor", label: "Monitor", icon: "ph-binoculars", yields: "monitor", credits: 3, flatCreditCost: true, pricingLabel: "3 credits first setup" },
    { key: "specialties", actionKey: "get-specialties", label: "Get specialties", icon: "ph-tag-chevron", yields: "specialties", credits: 0, pricingLabel: "No credits" },
    { key: "addresses", actionKey: "get-addresses", label: "Get addresses", icon: "ph-map-pin", yields: "addresses", credits: 0, pricingLabel: "No credits" },
    { key: "executives", actionKey: "get-executives", label: "Get executives", icon: "ph-crown", yields: "employees", credits: 0, pricingLabel: "No credits" },
  ],
  employees: [
    { key: "similar_people", actionKey: "get-similar-people", label: "Get Similar People", icon: "ph-users-three", yields: "employees", credits: 10, pricingLabel: "10 credit base + 5/search" },
    { key: "work_experience", actionKey: "get-work-experience", label: "Get Work Experience", icon: "ph-briefcase", yields: "work_experience", credits: 0, fallbackCredits: 3, pricingLabel: "No credits for enriched rows", fallbackPricingLabel: "3 credits/source when profile lookup is needed", forceCreditWarning: true },
    { key: "education_history", actionKey: "get-education-history", label: "Get Education History", icon: "ph-graduation-cap", yields: "education_history", credits: 0, fallbackCredits: 3, pricingLabel: "No credits for enriched rows", fallbackPricingLabel: "3 credits/source when profile lookup is needed", forceCreditWarning: true },
  ],
  updates: [],
};

const TRANSFORM_UI = {
  "get-competitors": { icon: "ph-flag" },
  "get-customers": { icon: "ph-handshake" },
  "get-funding": { icon: "ph-currency-circle-dollar" },
  "search-employees": { icon: "ph-magnifying-glass" },
  "get-updates": { icon: "ph-rss" },
  "monitor": { icon: "ph-binoculars" },
  "get-specialties": { icon: "ph-tag-chevron" },
  "get-addresses": { icon: "ph-map-pin" },
  "get-executives": { icon: "ph-crown" },
  "get-similar-people": { icon: "ph-users-three", label: "Get Similar People" },
  "get-work-experience": { icon: "ph-briefcase", label: "Get Work Experience" },
  "get-education-history": { icon: "ph-graduation-cap", label: "Get Education History" },
};

function normalizeTransforms(actionsByEntity) {
  return Object.fromEntries(
    Object.entries(actionsByEntity || {}).map(([entityType, actions]) => [
      entityType,
      (actions || []).map(action => ({
        ...action,
        ...(TRANSFORM_UI[action.actionKey] || {}),
      })),
    ])
  );
}

export const TRANSFORMS = Object.keys(PROSPECTOR_UI_SCHEMA.spreadsheetActions || {}).length
  ? normalizeTransforms(PROSPECTOR_UI_SCHEMA.spreadsheetActions)
  : DEMO_TRANSFORMS;

export const COLUMN_CATALOGUE = PROSPECTOR_UI_SCHEMA.columns
  || (typeof window !== "undefined" && window.PROSPECTOR_COLUMN_CATALOGUE)
  || {};

export const COLUMN_GROUP_ORDER = ["identity", "contact", "timing", "locations", "leadership", "social", "public_listed", "funding"];
export const COLUMN_GROUP_LABELS = {
  identity: "Identity",
  timing: "Timing",
  locations: "Locations",
  leadership: "Leadership",
  social: "Social",
  public_listed: "Public-listed",
  operations: "Operations",
  funding: "Funding",
  contact: "Contact",
  base: "Base",
};

export function catalogueFor(entityType) {
  return COLUMN_CATALOGUE[entityType] || [];
}

export function defaultColumnsFor(entityType) {
  const cat = catalogueFor(entityType);
  return cat.filter(c => c.default);
}

const DEMO_COLUMN_PRESETS = {
  companies: [
    { key: "logo", label: "", width: "w-12" },
    { key: "name", label: "Name", width: "w-44" },
    { key: "domain", label: "Domain", width: "w-44" },
  ],
  employees: [
    { key: "name", label: "Name", width: "w-44" },
    { key: "title", label: "Title", width: "w-44" },
    { key: "company", label: "Company", width: "w-40" },
    { key: "company_domain", label: "Company domain", width: "w-44" },
  ],
  updates: [
    { key: "company", label: "Company", width: "w-40" },
    { key: "company_domain", label: "Company domain", width: "w-44" },
    { key: "kind", label: "Type", width: "w-28" },
    { key: "title", label: "Title", width: "w-80" },
    { key: "published", label: "Published", width: "w-32" },
  ],
  funding: [
    { key: "source_company", label: "Company", width: "w-40" },
    { key: "source_domain", label: "Domain", width: "w-44" },
    { key: "stage", label: "Stage", width: "w-32" },
    { key: "date", label: "Date", width: "w-28" },
    { key: "amount_usd", label: "Round amount", width: "w-32" },
    { key: "investor_name", label: "Investor", width: "w-44" },
  ],
  specialties: [
    { key: "company", label: "Company", width: "w-40" },
    { key: "company_domain", label: "Company domain", width: "w-44" },
    { key: "specialty", label: "Specialty", width: "w-80" },
  ],
  addresses: [
    { key: "company", label: "Company", width: "w-40" },
    { key: "company_domain", label: "Company domain", width: "w-44" },
    { key: "address_type", label: "Type", width: "w-28" },
    { key: "line1", label: "Address", width: "w-80" },
    { key: "line2", label: "Address 2", width: "w-56" },
    { key: "city", label: "City", width: "w-32" },
    { key: "state", label: "State", width: "w-32" },
    { key: "postal_code", label: "Postal code", width: "w-28" },
    { key: "country", label: "Country", width: "w-32" },
    { key: "is_primary", label: "Primary", width: "w-24" },
  ],
  work_experience: [
    { key: "person", label: "Person", width: "w-44" },
    { key: "person_id", label: "Person ID", width: "w-52" },
    { key: "role", label: "Role", width: "w-44" },
    { key: "company", label: "Company", width: "w-44" },
    { key: "company_domain", label: "Company domain", width: "w-44" },
    { key: "start_date", label: "Start", width: "w-28" },
    { key: "end_date", label: "End", width: "w-28" },
    { key: "description", label: "Description", width: "w-80" },
  ],
  education_history: [
    { key: "person", label: "Person", width: "w-44" },
    { key: "person_id", label: "Person ID", width: "w-52" },
    { key: "school", label: "School", width: "w-56" },
    { key: "major", label: "Major", width: "w-56" },
    { key: "start_date", label: "Start", width: "w-28" },
    { key: "end_date", label: "End", width: "w-28" },
  ],
};

export const COLUMN_PRESETS = {
  companies: defaultColumnsFor("companies").length ? defaultColumnsFor("companies") : DEMO_COLUMN_PRESETS.companies,
  employees: defaultColumnsFor("employees").length ? defaultColumnsFor("employees") : DEMO_COLUMN_PRESETS.employees,
  updates: defaultColumnsFor("updates").length ? defaultColumnsFor("updates") : DEMO_COLUMN_PRESETS.updates,
  funding: defaultColumnsFor("funding").length ? defaultColumnsFor("funding") : DEMO_COLUMN_PRESETS.funding,
  specialties: defaultColumnsFor("specialties").length ? defaultColumnsFor("specialties") : DEMO_COLUMN_PRESETS.specialties,
  addresses: defaultColumnsFor("addresses").length ? defaultColumnsFor("addresses") : DEMO_COLUMN_PRESETS.addresses,
  work_experience: defaultColumnsFor("work_experience").length ? defaultColumnsFor("work_experience") : DEMO_COLUMN_PRESETS.work_experience,
  education_history: defaultColumnsFor("education_history").length ? defaultColumnsFor("education_history") : DEMO_COLUMN_PRESETS.education_history,
};

export const BASE_COLUMN_KEYS = Object.fromEntries(
  Object.entries(COLUMN_PRESETS).map(([k, cols]) => [k, new Set(cols.map(c => c.key))])
);
